import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Printer, Trash2, Pencil, Send, Loader2,
  FileSpreadsheet, FileText, RefreshCw, Search, X, CheckCircle, Home,
} from 'lucide-react';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EidResidenceRegistrations({ year, onChange, readOnly = false }) {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingReg, setEditingReg] = useState(null);

  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  const api = axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });

  const [form, setForm] = useState({ its: '', name: '', mobile: '', count: 1 });
  const [saving, setSaving] = useState(false);

  // Member-search state
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [memberSearchOpen, setMemberSearchOpen] = useState(false);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [memberPicked, setMemberPicked] = useState(false);

  const loadRegistrations = useCallback(async () => {
    if (!year) return;
    setLoading(true);
    try {
      const res = await api.get(`/eid-zabhiyat/${encodeURIComponent(year)}/residence/registrations`);
      setRegs(res.data.registrations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  useEffect(() => { loadRegistrations(); }, [loadRegistrations]);

  useEffect(() => {
    if (!showAdd || editingReg) return;
    const term = memberSearchTerm.trim();
    if (term.length < 2) { setMemberSearchResults([]); setMemberSearchOpen(false); return; }
    setMemberSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/members/search-fuzzy?q=${encodeURIComponent(term)}&limit=10`);
        setMemberSearchResults(res.data?.members || []);
        setMemberSearchOpen(true);
      } catch { setMemberSearchResults([]); }
      finally { setMemberSearchLoading(false); }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberSearchTerm, showAdd, editingReg]);

  useEffect(() => {
    if (!showAdd) { setMemberSearchTerm(''); setMemberSearchResults([]); setMemberSearchOpen(false); setMemberPicked(false); }
  }, [showAdd]);

  const pickMember = (m) => {
    const digits = String(m.mobile || '').replace(/\D/g, '');
    const last10 = digits.length >= 10 ? digits.slice(-10) : digits;
    setForm(prev => ({
      ...prev,
      name: m.full_name || prev.name,
      its: (m.its_id || '').toString().replace(/\D/g, '').slice(0, 8) || prev.its,
      mobile: last10 || prev.mobile,
    }));
    setMemberSearchTerm(m.full_name || '');
    setMemberSearchOpen(false);
    setMemberPicked(true);
  };

  const clearPickedMember = () => {
    setMemberPicked(false); setMemberSearchTerm(''); setMemberSearchResults([]); setMemberSearchOpen(false);
  };

  const openAdd = () => {
    setEditingReg(null);
    setForm({ its: '', name: '', mobile: '', count: 1 });
    setShowAdd(true);
  };

  const openEdit = (reg) => {
    setEditingReg(reg);
    setForm({ its: reg.its || '', name: reg.name || '', mobile: reg.mobile || '', count: 1 });
    setShowAdd(true);
  };

  const submitForm = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const mob = (form.mobile || '').replace(/\D/g, '');
    if (mob.length !== 10) { toast.error('Mobile must be exactly 10 digits'); return; }
    if (form.its && !/^\d{8}$/.test(form.its)) { toast.error('ITS must be exactly 8 digits'); return; }
    const cnt = parseInt(form.count, 10);
    if (!Number.isFinite(cnt) || cnt < 1) { toast.error('Count must be at least 1'); return; }

    setSaving(true);
    try {
      if (editingReg) {
        await api.put(`/eid-zabhiyat/${encodeURIComponent(year)}/residence/registrations/${editingReg.id}`, {
          its: form.its, name: form.name.trim(), mobile: mob,
        });
        toast.success('Updated');
      } else {
        const resp = await api.post(`/eid-zabhiyat/${encodeURIComponent(year)}/residence/register`, {
          year, its: form.its, name: form.name.trim(), mobile: mob, count: cnt,
        });
        toast.success(`${cnt} residence registration(s) created. WhatsApp sent.`);
        const firstReg = resp.data?.registrations?.[0];
        if (firstReg?.id) openReceipt(firstReg.id);
      }
      setShowAdd(false);
      loadRegistrations();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const openReceipt = (regId) => {
    api.get(`/eid-zabhiyat/${encodeURIComponent(year)}/residence/registrations/${regId}/receipt`, {
      responseType: 'text',
      headers: { Accept: 'text/html' },
    })
      .then((res) => {
        const blob = new Blob([res.data], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        iframe.src = url;
        document.body.appendChild(iframe);
        setTimeout(() => { try { URL.revokeObjectURL(url); iframe.remove(); } catch {} }, 60_000);
      })
      .catch(() => toast.error('Could not load receipt'));
  };

  const handleDelete = async (reg) => {
    if (!window.confirm(`Delete this residence registration?`)) return;
    try {
      await api.delete(`/eid-zabhiyat/${encodeURIComponent(year)}/residence/registrations/${reg.id}`);
      toast.success('Deleted');
      loadRegistrations();
      if (onChange) onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const handleResend = async (reg) => {
    try {
      await api.post(`/eid-zabhiyat/${encodeURIComponent(year)}/residence/registrations/${reg.id}/resend-whatsapp`);
      toast.success('WhatsApp resend triggered');
      setTimeout(loadRegistrations, 3500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Resend failed'); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get(`/eid-zabhiyat/${encodeURIComponent(year)}/residence/export?format=excel`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
      const a = document.createElement('a'); a.href = url; a.download = `EidZabhiyat_Residence_${year}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Excel export failed'); }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/eid-zabhiyat/${encodeURIComponent(year)}/residence/export?format=pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `EidZabhiyat_Residence_${year}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF export failed'); }
  };

  const printList = async () => {
    try {
      const res = await api.get(`/eid-zabhiyat/${encodeURIComponent(year)}/residence/export?format=pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch {} }, 600);
      setTimeout(() => { try { URL.revokeObjectURL(url); iframe.remove(); } catch {} }, 60_000);
    } catch { toast.error('Print failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={loadRegistrations} data-testid="eid-res-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={exportExcel} disabled={!regs.length} data-testid="eid-res-export-excel">
          <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
        </Button>
        <Button variant="outline" size="sm" onClick={exportPdf} disabled={!regs.length} data-testid="eid-res-export-pdf">
          <FileText className="w-4 h-4 mr-1" /> PDF
        </Button>
        <Button variant="outline" size="sm" onClick={printList} disabled={!regs.length} data-testid="eid-res-print-list">
          <Printer className="w-4 h-4 mr-1" /> Print List
        </Button>
        <Button onClick={openAdd} disabled={readOnly} data-testid="eid-res-add-btn">
          <Plus className="w-4 h-4 mr-1" /> New Residence Registration
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
          ) : regs.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Home className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              No residence registrations yet for {year}. Click <strong>New Residence Registration</strong> to add the first one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>ITS</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>WhatsApp</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {regs.map((r, i) => (
                  <TableRow key={r.id} data-testid={`eid-res-row-${i + 1}`}>
                    <TableCell className="font-bold">{i + 1}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.its || '-'}</TableCell>
                    <TableCell>{r.mobile}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {r.registration_date ? new Date(r.registration_date).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.whatsapp_status === 'sent' ? 'default' : r.whatsapp_status === 'failed' ? 'destructive' : 'secondary'}>
                        {r.whatsapp_status || 'pending'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openReceipt(r.id)} title="Print receipt">
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleResend(r)} title="Resend WhatsApp">
                          <Send className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)} title="Edit">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(r)} className="text-red-500" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAdd} onOpenChange={(o) => !o && setShowAdd(false)}>
        <DialogContent className="max-w-md" data-testid="eid-res-dialog">
          <DialogHeader>
            <DialogTitle>{editingReg ? 'Edit Residence Registration' : 'New Residence Registration'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-4">
            {!editingReg && (
              <div className="relative">
                <Label className="flex items-center gap-1">
                  <Search className="w-3.5 h-3.5" /> Search Member (Name / ITS / Mobile)
                </Label>
                <div className="relative mt-1">
                  <Input
                    value={memberSearchTerm}
                    onChange={(e) => { setMemberSearchTerm(e.target.value); setMemberPicked(false); }}
                    onFocus={() => { if (memberSearchResults.length > 0) setMemberSearchOpen(true); }}
                    placeholder="Type to auto-fetch name, ITS & mobile"
                    className={memberPicked ? 'border-emerald-400 pr-10' : 'pr-10'}
                    data-testid="eid-res-member-search"
                  />
                  {memberSearchLoading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
                  {!memberSearchLoading && memberPicked && (
                    <button type="button" onClick={clearPickedMember} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-emerald-600 hover:text-red-500" title="Clear">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {!memberSearchLoading && !memberPicked && memberSearchTerm.length >= 2 && memberSearchOpen && (
                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto">
                      {memberSearchResults.length === 0 ? (
                        <div className="p-3 text-sm text-slate-500">No match. Enter details manually below.</div>
                      ) : (
                        memberSearchResults.map((m) => (
                          <button key={m.id} type="button" onClick={() => pickMember(m)} className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b last:border-b-0 border-slate-100">
                            <div className="text-sm font-medium text-slate-800">{m.full_name}</div>
                            <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                              <span>ITS: {m.its_id || '-'}</span><span>{m.mobile || '-'}</span>
                              {m.mohalla && <span>{m.mohalla}</span>}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {memberPicked && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Member selected — fields below auto-filled.
                  </p>
                )}
              </div>
            )}
            <div>
              <Label>ITS Number (optional)</Label>
              <Input value={form.its} onChange={(e) => setForm({ ...form, its: e.target.value.replace(/\D/g, '').slice(0, 8) })} placeholder="8-digit ITS" inputMode="numeric" data-testid="eid-res-its" />
            </div>
            <div>
              <Label>Full Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="eid-res-name" />
            </div>
            <div>
              <Label>Mobile Number * (10 digits)</Label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })} inputMode="numeric" maxLength={10} required data-testid="eid-res-mobile" />
            </div>
            {!editingReg && (
              <div>
                <Label>No. of Residence Zabhiyat *</Label>
                <Input type="number" min={1} max={50} value={form.count} onChange={(e) => setForm({ ...form, count: Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1)) })} data-testid="eid-res-count" />
              </div>
            )}
            <div className="text-xs text-slate-500">
              Registration date: <span className="font-medium">{new Date().toLocaleDateString()}</span> (auto)
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="eid-res-submit">
                {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>) : (editingReg ? 'Update' : 'Register & Print Receipt')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
